import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { useAuth } from "@/features/auth/context/AuthContext";
import { MoreVertical, UserX } from "lucide-react";
import { useNavigate } from "react-router-dom";

export type UserRole = "superuser" | "admin" | "staff";

export interface Profile {
  id: string;
  given_name: string;
  family_name: string;
  role: UserRole;
}

interface HeaderActionsProps {
  variant?: "avatar" | "menu";
  className?: string;
}

export function HeaderActions({
  variant = "avatar",
  className
}: HeaderActionsProps) {
  const { signOut, impersonatedUser, exitImpersonation, profile } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleExitImpersonation = () => {
    exitImpersonation();
  };

  if (variant === "menu") {
    return (
      <div className={`flex items-center gap-2 ${className || ""}`}>
        <LanguageSwitcher />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {impersonatedUser && (
              <>
                <DropdownMenuItem onClick={handleExitImpersonation}>
                  <UserX className="w-4 h-4 mr-2" />
                  Impersonierung beenden
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className || ""}`}>
      <LanguageSwitcher />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarImage src="/placeholder.svg?height=32&width=32" />
              <AvatarFallback>
                {profile
                  ? `${profile.given_name?.[0] || ""}${profile.family_name?.[0] || ""}`
                  : "U"}
              </AvatarFallback>
            </Avatar>
            {impersonatedUser && (
              <div className="absolute -top-1 -right-1 h-4 w-4 bg-orange-500 rounded-full flex items-center justify-center">
                <UserX className="h-2.5 w-2.5 text-white" />
              </div>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="text-sm font-medium">
              {profile
                ? `${profile.given_name} ${profile.family_name}`
                : "User"}
            </div>
            {impersonatedUser && (
              <div className="text-xs text-orange-600 font-medium mt-1 flex items-center">
                <UserX className="h-3 w-3 mr-1" />
                Impersonierungsmodus
              </div>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {impersonatedUser && (
            <>
              <DropdownMenuItem onClick={handleExitImpersonation}>
                <UserX className="mr-2 h-4 w-4" />
                Impersonierung beenden
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
